import React, { useState, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useKeenSlider } from 'keen-slider/react';
import { Link } from 'react-router-dom';

interface Review {
  id: number;
  author: string;
  avatar: string;
  rating: number;
  date: string;
  text: string;
  product?: string;
  productId?: string;
}

const reviews: Review[] = [
  {
    id: 1,
    author: "Kadri Tamm",
    avatar: "https://res.cloudinary.com/daka2lob2/image/upload/v1751274191/Kadri_Tamm.1_tuaqqg.png",
    rating: 5,
    date: "18. mai 2025",
    text: "Ostsin oma 12-aastasele pojale ja ta on väga rahul. Tõuks on kerge, kuid vastupidav, sobib nii algajatele kui ka neile, kes juba oskavad trikke teha. Kindlasti ostan siit veel",
    product: "Longway Metro - Kuld",
    productId: "longway-metro-trikitoukeratas-kuld"
  },
  {
    id: 2,
    author: "Marten Rebane",
    avatar: "https://res.cloudinary.com/daka2lob2/image/upload/v1751274201/Marten_Rebane.1_zreiwi.jpg",
    rating: 5,
    date: "9. aprill 2025",
    text: "Tellisin kaks tõuksi, ühe endale ja teise vennale. Mõlemad on super kvaliteediga ja hinnaklassi arvestades parim valik turul, soovitan kõigile!",
    product: "Blunt Prodigy X - Õlilaik",
    productId: "blunt-prodigy-x-oil-slick"
  },
  {
    id: 3,
    author: "Kristjan Kask",
    avatar: "https://res.cloudinary.com/daka2lob2/image/upload/v1751274195/Kristjan_Kask.1_dqkxb1.jpg",
    rating: 5,
    date: "29. aprill 2025",
    text: "Siit leiab head kraami! Soovitan soojalt Tõuks.ee kõigile, sest ei näe kuskil nii laia valikut tõuksidest",
    product: "AZTEK Siren - Zircon Sand",
    productId: "aztek-siren-zircon-sand"
  },
  {
    id: 4,
    author: "Markus Tammik",
    avatar: "https://res.cloudinary.com/daka2lob2/image/upload/v1751274199/Markus_Tammik_q70l4b.jpg",
    rating: 5,
    date: "20. märts 2025",
    text: "Siit sain hea ja korraliku tõuksi, ostan siit veelgi",
    product: "Drone Shadow 3 Feather-Light - Hõbe",
    productId: "drone-shadow-3-feather-light-silver-anodized"
  },
  {
    id: 5,
    author: "Laura Kasemaa",
    avatar: "https://res.cloudinary.com/daka2lob2/image/upload/v1751274197/Laura_Kasemaa_i7luvl.jpg",
    rating: 5,
    date: "4. mai 2025",
    text: "Sain pojale siit suurepärase tõukeratta. Valik on lai ning poiss on rahul!",
    product: "Core CL1 Mini - Must",
    productId: "core-cl1-mini-trikitoukeratas-must"
  }
];

interface CustomerReviewsProps {
  productFilter?: string;
  compact?: boolean;
  customProductId?: string;
  galleryImages?: string[];
}

interface ImageGalleryProps {
  images: string[];
  autoSlideInterval?: number;
}

function ImageGallery({ images, autoSlideInterval = 6000 }: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoSlideRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-slide functionality (1.5x slower)
  React.useEffect(() => {
    if (images.length <= 1 || isDragging) {
      if (autoSlideRef.current) {
        clearInterval(autoSlideRef.current);
        autoSlideRef.current = null;
      }
      return;
    }

    autoSlideRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const maxIndex = images.length - 1;
        return prevIndex >= maxIndex ? 0 : prevIndex + 1;
      });
    }, autoSlideInterval);

    return () => {
      if (autoSlideRef.current) {
        clearInterval(autoSlideRef.current);
        autoSlideRef.current = null;
      }
    };
  }, [images.length, autoSlideInterval, isDragging]);

  const nextSlide = () => {
    const maxIndex = images.length - 1;
    setCurrentIndex((prevIndex) => (prevIndex >= maxIndex ? 0 : prevIndex + 1));
  };

  const prevSlide = () => {
    const maxIndex = images.length - 1;
    setCurrentIndex((prevIndex) => (prevIndex <= 0 ? maxIndex : prevIndex - 1));
  };

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.clientX);
    setCurrentX(e.clientX);
    setDragOffset(0);
    
    // Stop auto-slide when dragging starts
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
      autoSlideRef.current = null;
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    
    setCurrentX(e.clientX);
    const diff = e.clientX - startX;
    setDragOffset(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const threshold = 50; // Minimum drag distance to trigger slide
    const diff = currentX - startX;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Dragged right - go to previous
        prevSlide();
      } else {
        // Dragged left - go to next
        nextSlide();
      }
    }
    
    setDragOffset(0);
    setStartX(0);
    setCurrentX(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
    setDragOffset(0);
    
    // Stop auto-slide when dragging starts
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
      autoSlideRef.current = null;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    setCurrentX(e.touches[0].clientX);
    const diff = e.touches[0].clientX - startX;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const threshold = 50;
    const diff = currentX - startX;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }
    
    setDragOffset(0);
    setStartX(0);
    setCurrentX(0);
  };

  if (images.length === 0) return null;

  // Mobile: show 1 image, Desktop: show 4 images
  const getImageWidth = () => {
    return window.innerWidth < 768 ? '100%' : '25%';
  };

  const getTranslateX = () => {
    if (window.innerWidth < 768) {
      // Mobile: move by 100% per slide
      return -currentIndex * 100 + (dragOffset / (containerRef.current?.offsetWidth || 1)) * 100;
    } else {
      // Desktop: move by 25% per slide (showing 4 at a time)
      const maxIndex = Math.max(0, images.length - 4);
      const clampedIndex = Math.min(currentIndex, maxIndex);
      return -clampedIndex * 25 + (dragOffset / (containerRef.current?.offsetWidth || 1)) * 100;
    }
  };

  const getTotalPages = () => {
    return window.innerWidth < 768 ? images.length : Math.max(1, images.length - 3);
  };

  const getCurrentPage = () => {
    if (window.innerWidth < 768) {
      return currentIndex + 1;
    } else {
      const maxIndex = Math.max(0, images.length - 4);
      return Math.min(currentIndex, maxIndex) + 1;
    }
  };

  return (
    <div className="relative w-full h-60 md:h-52">
      <div 
        ref={containerRef}
        className={`overflow-hidden h-full select-none ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className={`flex h-full ${
            isDragging ? '' : 'transition-transform duration-700 ease-in-out'
          }`}
          style={{
            transform: `translateX(${getTranslateX()}%)`
          }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="w-full md:w-1/4 flex-shrink-0 px-2 h-full"
            >
              <div className="w-full h-full overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                <img
                  src={image}
                  alt={`Gallery image ${index + 1}`}
                  title={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 select-none pointer-events-none"
                  draggable={false}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (!target.src.includes('https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg')) {
                      target.src = 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg';
                    }
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows - show on mobile and desktop when needed */}
      {((window.innerWidth < 768 && images.length > 1) || (window.innerWidth >= 768 && images.length > 4)) && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 z-10"
            aria-label="Previous images"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 z-10"
            aria-label="Next images"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </>
      )}

    </div>
  );
}

export function CustomerReviews({ productFilter, compact = false, customProductId, galleryImages }: CustomerReviewsProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Filter reviews for specific product if provided
  const filteredReviews = productFilter
    ? reviews.filter(review => review.product && review.product.includes(productFilter))
    : reviews;
  
  // If there are no reviews for this specific product, show all reviews
  const displayReviews = filteredReviews.length > 0 ? filteredReviews : reviews;
  
  const [sliderRef, instanceRef] = useKeenSlider({
    slides: {
      perView: 1,
      spacing: 16,
    },
    breakpoints: {
      "(min-width: 768px)": {
         slides: { perView: compact ? 1 : 2, spacing: 16 },
       },
      "(min-width: 1024px)": {
         slides: { perView: compact ? 1 : 3, spacing: 16 },
      },
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
  });

  const maxSlide = instanceRef.current ? 
    instanceRef.current.track.details.maxIdx : 
    displayReviews.length - 1;

  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`${compact ? 'py-6' : 'py-16'} bg-gray-50 rounded-xl`}>
      <div className="container mx-auto px-4 md:px-6">
        {!compact && (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Klientide tagasiside</h2>
          </div>
        )}
        
        {displayReviews.length > 0 ? (
          <div className="relative">
            <div ref={sliderRef} className="keen-slider">
              {displayReviews.map((review) => (
                <div key={review.id} className="keen-slider__slide">
                  <div className="bg-white p-6 rounded-lg shadow-md h-full flex flex-col">
                    <div className="flex items-center mb-4">
                      <img
                        src={review.avatar}
                        alt={review.author}
                        title={review.author}
                        className="w-12 h-12 rounded-full object-cover mr-4 bg-gray-100 border border-gray-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (!target.src.includes('https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg')) {
                            target.src = 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg';
                          }
                        }}
                      />
                      <div>
                        <h3 className="font-semibold">{review.author}</h3>
                        <div className="flex items-center">
                          {renderStars(review.rating)}
                          <span className="text-xs text-gray-500 ml-2">{review.date}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 flex-grow">{review.text}</p>
                    
                    {review.product && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <span className="text-sm text-gray-500">Toode: </span>
                        {review.productId || customProductId ? (
                          <Link 
                            to={`/tooted/${customProductId || review.productId}`} 
                            className="text-sm font-medium text-[#4FC1E9] hover:underline"
                          >
                            {review.product}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium text-[#4FC1E9]">{review.product}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {displayReviews.length > (compact ? 1 : 3) && (
              <>
                <button 
                  onClick={() => instanceRef.current?.prev()}
                  disabled={currentSlide === 0}
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-md disabled:opacity-50 disabled:cursor-not-allowed z-10 min-w-[44px] min-h-[44px]"
                  aria-label="Eelmine arvustus"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                
                <button 
                  onClick={() => instanceRef.current?.next()}
                  disabled={currentSlide === maxSlide}
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-md disabled:opacity-50 disabled:cursor-not-allowed z-10 min-w-[44px] min-h-[44px]"
                  aria-label="Järgmine arvustus"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Sellele tootele pole veel arvustusi.</p>
          </div>
        )}

        {/* Image Gallery Section - integrated into the reviews section */}
        {galleryImages && galleryImages.length > 0 && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <ImageGallery images={galleryImages} autoSlideInterval={6000} />
          </div>
        )}
      </div>
    </div>
  );
}
